import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Button, Spin, Alert, Space, Form, Input, Select, Table, Tag, message } from 'antd';
import {
  ArrowLeftOutlined, UserOutlined, TagsOutlined, ShoppingOutlined, SaveOutlined, TeamOutlined, EnvironmentOutlined,
} from '@ant-design/icons';
import { api } from '../../services/api';
import { useClient } from '../../context/ClientContext';
import { SectionCard } from '../../shared/components/SectionCard';
import { EstadoPedidoChip } from '../../shared/components/EstadoPedidoChip';
import { EstadoPagoChip } from '../../shared/components/EstadoPagoChip';
import { UsuariosCliente } from '../../shared/components/UsuariosCliente';
import { DomiciliosCliente } from '../../shared/components/DomiciliosCliente';

const { Text } = Typography;

const fecha = (iso) => (iso ? new Date(iso).toLocaleDateString('es-AR') : '—');
const pesos = (n) => `$ ${Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`;

export const ClienteDetalle = () => {
  const { id } = useParams();
  const { clientPath } = useClient();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [datos, setDatos] = useState(null);
  const [listas, setListas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [cambiandoLista, setCambiandoLista] = useState(false);

  const cargar = useCallback(async () => {
    const res = await api.admin.cliente(id);
    if (res.status !== 'success') {
      setError(res.message || 'No se pudo cargar el cliente');
      return;
    }
    setDatos(res.data);
    const c = res.data.cliente;
    form.setFieldsValue({
      nombre: c.nombre, dni: c.dni || '', cuit: c.cuit || '',
      mail: c.mail || '', telefono: c.telefono || '', domicilio: c.domicilio || '',
    });
  }, [id, form]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([
        cargar(),
        api.admin.listasVenta().then((r) => { if (r.status === 'success') setListas(r.data || []); }),
      ]);
      setLoading(false);
    })();
  }, [cargar]);

  const guardar = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setGuardando(true);
    try {
      const res = await api.admin.editarCliente(id, values);
      if (res.status === 'success') {
        message.success('Cliente guardado');
        await cargar();
      } else {
        message.error(res.message || 'No se pudo guardar');
      }
    } finally {
      setGuardando(false);
    }
  };

  const cambiarLista = async (idLista) => {
    setCambiandoLista(true);
    try {
      const res = await api.admin.setListaCliente(id, idLista);
      if (res.status === 'success') {
        message.success('Lista de precio actualizada');
        setDatos((d) => ({ ...d, cliente: { ...d.cliente, idpreciolista: idLista } }));
      } else {
        message.error(res.message || 'No se pudo cambiar la lista');
      }
    } finally {
      setCambiandoLista(false);
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 48 }}><Spin size="large" /></div>;
  if (error) return <Alert type="error" message={error} />;
  if (!datos) return null;

  const { cliente, pedidos, usuarios = [], domicilios = [], editable } = datos;

  const columnasPedidos = [
    { title: 'Pedido', dataIndex: 'id', key: 'id', width: 90, render: (v) => <Text strong>#{v}</Text> },
    { title: 'Fecha', dataIndex: 'fecha_alta', key: 'fecha', width: 110, render: fecha },
    {
      title: 'Origen', dataIndex: 'origen', key: 'origen', width: 100,
      render: (v) => (v === 'admin' ? <Tag>Vendedor</Tag> : <Tag color="blue">Web</Tag>),
    },
    {
      title: 'Estado', key: 'estado', width: 150,
      render: (_, p) => <EstadoPedidoChip nombre={p.estado} color={p.estado_color} />,
    },
    {
      title: 'Pago', key: 'pago', width: 150,
      render: (_, p) => <EstadoPagoChip nombre={p.pago} color={p.pago_color} />,
    },
    {
      title: 'Total', dataIndex: 'total', key: 'total', width: 130, align: 'right',
      render: (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{pesos(v)}</span>,
    },
  ];

  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <Button icon={<ArrowLeftOutlined />} type="text" style={{ marginLeft: -8 }}
          onClick={() => navigate(clientPath('/admin/clientes'))} />
        <Text strong style={{ fontSize: 28, lineHeight: 1.2 }}>{cliente.nombre}</Text>
        {cliente.usuario_web && <Tag color="blue">Cuenta web</Tag>}
        {cliente.idcliente_erp && <Tag>ERP #{cliente.idcliente_erp}</Tag>}
      </div>

      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <SectionCard
          icon={<UserOutlined />}
          title="Datos"
          extra={editable && (
            <Button type="primary" size="small" icon={<SaveOutlined />} loading={guardando} onClick={guardar}>
              Guardar
            </Button>
          )}
        >
          {!editable && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="Este cliente viene del ERP: sus datos se editan allá."
            />
          )}
          {cliente.usuario_web && (
            <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 13 }}>
              Entra a la web como {cliente.usuario_web}
            </Text>
          )}
          <Form form={form} layout="vertical" disabled={!editable}>
            <Form.Item label="Nombre o razón social" name="nombre"
              rules={[{ required: true, whitespace: true, message: 'El nombre es obligatorio' }]}>
              <Input />
            </Form.Item>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Form.Item label="DNI" name="dni" style={{ flex: '1 1 160px' }}>
                <Input inputMode="numeric" />
              </Form.Item>
              <Form.Item label="CUIT" name="cuit" style={{ flex: '1 1 160px' }}>
                <Input />
              </Form.Item>
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Form.Item label="Mail" name="mail" rules={[{ type: 'email', message: 'Revisá el mail' }]}
                style={{ flex: '1 1 200px' }}>
                <Input />
              </Form.Item>
              <Form.Item label="Teléfono" name="telefono" style={{ flex: '1 1 140px' }}>
                <Input />
              </Form.Item>
            </div>
            <Form.Item label="Domicilio" name="domicilio" style={{ marginBottom: 4 }}>
              <Input />
            </Form.Item>
          </Form>
        </SectionCard>

        <SectionCard icon={<TagsOutlined />} title="Lista de precio">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Select
              value={cliente.idpreciolista || 1}
              onChange={cambiarLista}
              loading={cambiandoLista}
              style={{ width: 220 }}
              options={listas.map((l) => ({ value: l.id, label: l.nombre }))}
            />
            <Text type="secondary" style={{ fontSize: 13 }}>
              Es la lista con la que ve los precios en la web y con la que se le cargan los pedidos.
            </Text>
          </div>
        </SectionCard>

        <SectionCard icon={<EnvironmentOutlined />} title={`Domicilios (${domicilios.length})`}>
          <DomiciliosCliente
            domicilios={domicilios}
            sugerido={{ dni: cliente.dni || '', telefono_contacto: cliente.telefono || '' }}
            crear={async (data) => {
              const res = await api.admin.crearDomicilioCliente(id, data);
              if (res.status === 'success') await cargar();
              return res;
            }}
            editar={async (did, data) => {
              const res = await api.admin.editarDomicilioCliente(id, did, data);
              if (res.status === 'success') await cargar();
              return res;
            }}
            quitar={async (did) => {
              const res = await api.admin.quitarDomicilioCliente(id, did);
              if (res.status === 'success') await cargar();
              return res;
            }}
          />
        </SectionCard>

        <SectionCard icon={<TeamOutlined />} title={`Usuarios (${usuarios.length})`}>
          <UsuariosCliente
            usuarios={usuarios}
            ayuda="Las personas que compran como este cliente desde la web: ven sus pedidos y compran con su lista. Si alguien todavía no tiene cuenta, queda adentro la primera vez que entre con ese mail."
            agregar={async (mail) => {
              const res = await api.admin.agregarUsuarioCliente(id, mail);
              if (res.status === 'success') await cargar();
              return res;
            }}
            quitar={async (uid) => {
              const res = await api.admin.quitarUsuarioCliente(id, uid);
              if (res.status === 'success') await cargar();
              return res;
            }}
          />
        </SectionCard>

        <SectionCard icon={<ShoppingOutlined />} title={`Pedidos (${cliente.pedidos})`}>
          <Table
            columns={columnasPedidos}
            dataSource={pedidos}
            rowKey="id"
            size="small"
            pagination={pedidos.length > 10 ? { pageSize: 10, showSizeChanger: false } : false}
            scroll={{ x: 'max-content' }}
            onRow={(p) => ({
              onClick: () => navigate(clientPath(`/admin/pedidos/${p.id}`)),
              style: { cursor: 'pointer' },
            })}
            locale={{ emptyText: 'Todavía no hizo pedidos.' }}
          />
        </SectionCard>
      </Space>
    </div>
  );
};
